/**
 * 실시간 버스를 **시간의 함수**로 그린다.
 *
 * **왜 갈아엎었나.** 관측과 화면이 어긋났을 때 어떻게 할지를 여섯 군데서 따로
 * 답하고 있었다 - `maxDeadReckoningAge` · `MAX_SEGMENTS` · `PHASE_PACE` ·
 * `MAX_PHASE` · `busResync` 의 앞섬/뒤처짐 분기 · `catchUpRange`. 하나를 고치면
 * 다른 증상이 나왔다 (순간이동 -> 기어감 -> 얼어붙음, 2026-09-14~15 하루에 네 번).
 *
 * 레퍼런스의 표준 분해는 `Tracker -> Filter -> Predictor` 인데(Cathey & Dailey
 * 2003) 우리는 **Filter 자리가 비어 있었고** 그 자리를 손잡이로 때우고 있었다.
 *
 * 여기서는 상태를 하나로 둔다: **궤적의 닻(anchor)**. 관측이 오면 새 닻을 놓고,
 * 옛 궤적에서 새 궤적으로 `BLEND_MS` 동안 섞는다.
 *
 *     s(t) = lerp(옛궤적(t), 새궤적(t), smoothstep((t - t_obs) / BLEND_MS))
 *
 *   - 얼어붙지 않는다 - 궤적은 계속 전진한다 (구간 수 상한이 필요 없다)
 *   - **뒤로도 간다** - 관측이 화면보다 뒤면 블렌드가 되돌린다 (2026-09-16)
 *   - 순간이동 안 한다 - 보정이 블렌드로 흡수된다
 *   - 기어가지 않는다 - 접근 감속 같은 보정이 필요 없다
 *   - 남는 손잡이는 `BLEND_MS` 하나다
 *
 * ⚠️ **칼만이 아닌 이유.** 우리 관측은 위치가 부정확한 게 아니라 **시각이
 * 양자화**돼 있다 (버스가 그 정류장에 있었던 건 확실하고, 모르는 건 언제 지났나다).
 * 그래서 위치 잡음 R 이 작아 K ~ 1 이 되고 칼만은 개루프 모델과 같아진다 -
 * 2026-09-14 에 실시간 배율 칼만을 실제로 만들어 재봤고 +0.7% 였다
 * (`seoul-bus-data/tools/live-scale.py`). 필요한 건 더 똑똑한 추정기가 아니라
 * **어긋남을 화면에 녹이는 한 곳**이다.
 */

// 새 관측으로 갈아타는 **최소** 시간. 짧으면 순간이동으로 보이고 길면 옛 위치를
// 오래 붙든다. 작은 보정은 이 값으로 끝난다.
export const BLEND_MS = 15000;

// 보정이 따라잡는 속도 상한(m/s). **시간을 고정하면 화면속도가 보정 거리에
// 비례해 버린다** - 실측(2026-09-16, 12건)에서 400~1,312m 보정이 15초에 걸려
// 103~315 km/h 로 날아갔고 그게 사용자가 본 '순간이동' 이었다. 거리를 시간으로
// 나누지 말고 **속도를 묶고 시간을 늘린다**.
//
// 20 m/s = 72 km/h. 버스 자체 중위 주행이 15 km/h 라 따라잡는 동안은 확실히
// 빠르게 보이지만, 관측 간격(60~300초) 안에 수렴해야 다음 관측이 처음부터
// 다시 시작하지 않는다. 상한을 더 낮추면 영영 못 따라잡는다.
export const CATCHUP_MPS = 20;

/**
 * 이번 보정에 쓸 블렌드 시간.
 * @param {number} gap - 그리던 자리와 새 궤적의 차이(m)
 * @returns {number} ms
 */
export function blendMs(gap) {
    return Math.max(BLEND_MS, Math.abs(gap) / CATCHUP_MPS * 1000);
}

/**
 * 시간표 페이스로 `dt` 만큼 전진한 누적거리.
 *
 * **구간마다 자기 페이스(span/duration)를 쓴다** - 고속 구간과 도심 구간이 섞여
 * 있어도 맞다. 종점에 닿으면 거기서 멈춘다.
 *
 * @param {number} s0 - 출발 누적거리
 * @param {Array<number>} offsets - 정류장별 누적거리 (오름차순)
 * @param {Array<number>} times - 정류장별 시각 (offsets 와 같은 인덱스)
 * @param {number} dt - 흘려보낼 시간 (ms)
 * @returns {number} 전진한 누적거리
 */
export function advance(s0, offsets, times, dt) {
    if (!offsets || !times || offsets.length !== times.length || offsets.length < 2 ||
        !Number.isFinite(s0) || !(dt > 0)) {
        return s0;
    }

    let i = 0;

    while (i < offsets.length - 2 && offsets[i + 1] <= s0) {
        i++;
    }

    let s = s0,
        left = dt;

    while (left > 0 && i < offsets.length - 1) {
        const span = offsets[i + 1] - offsets[i],
            duration = times[i + 1] - times[i];

        if (!(span > 0) || !(duration > 0)) {
            break;
        }

        const pace = span / duration,
            toEnd = (offsets[i + 1] - s) / pace;

        if (toEnd >= left) {
            return s + pace * left;
        }
        s = offsets[i + 1];
        left -= toEnd;
        i++;
    }
    return s;
}

/**
 * 0~1 을 부드럽게 (양 끝 기울기 0). 선형이면 갈아타는 순간이 각지게 보인다.
 * @param {number} x - 진행률
 * @returns {number} 0~1
 */
export function smoothstep(x) {
    const t = Math.max(0, Math.min(1, x));

    return t * t * (3 - 2 * t);
}

/**
 * 지금 그려야 할 누적거리.
 *
 * @param {Object} anchor - 지금 궤적의 닻 `{t, s}` (관측 시각과 그때의 누적거리)
 * @param {?Object} prev - 직전 궤적의 닻 `{t, s, ms}` (`ms` = 이번 보정의 블렌드 시간, 없으면 `BLEND_MS`). null 이면 블렌드 자체를 안 한다
 * @param {Array<number>} offsets - 정류장별 누적거리
 * @param {Array<number>} times - 정류장별 시각
 * @param {number} now - 지금 (ms)
 * @returns {number} 누적거리
 */
export function positionAt(anchor, prev, offsets, times, now) {
    // ⚠️⚠️ **블렌드 진행은 `anchor.t`(관측 시각)가 아니라 `prev.t`(갈아탄
    // 시각)로 잰다.** 관측은 도착할 때 이미 60~300초 낡아 있어서(실측 중위
    // 42~98초) `now - anchor.t` 는 처음부터 `BLEND_MS`(15초)를 넘는다 -
    // 그러면 `w` 가 곧장 1 이 되어 **블렌드가 한 번도 안 돈다.** 갈아타는
    // 순간 새 궤적으로 점프한다는 뜻이다. `prev.t` 는 갈아탄 그 시각이라
    // (map.js `bus.prevAnchor = {t: now, ...}`) 거기서부터 재야 맞다.
    // 2026-09-16 발견. 뒤로 가기를 켜기 전에는 `floor` 가 뒤쪽 점프만 막아
    // 증상이 '얼어붙음' 으로 보였고, 앞쪽 점프는 그냥 순간이동이었다.
    const target = advance(anchor.s, offsets, times, now - anchor.t),
        w = prev ? smoothstep((now - prev.t) / (prev.ms || BLEND_MS)) : 1;

    // **뒤로도 간다 (2026-09-16).** 관측이 화면보다 뒤를 가리키면(우리가 앞서
    // 그렸다) 블렌드가 그 차이를 뒤로 미끄러뜨린다. 예전에는 `floor` 로 막고
    // 세워 뒀는데, 실측으로 **연속 관측쌍의 14% 가 그 바닥에 걸려 멈췄다**
    // (2026-09-15, 40,526쌍). 사용자 판단으로 '멈춤' 보다 '되돌아감' 을 택했다.
    // 되돌아감은 최대 `BLEND_MS` 동안만이다 - 그 뒤엔 새 궤적이 전진한다.
    return w >= 1 ? target :
        advance(prev.s, offsets, times, now - prev.t) * (1 - w) + target * w;
}

/**
 * 피드에서 사라졌는데 **같은 차량이 다른 트립으로 살아 있는** 버스들.
 *
 * 회차에서 한 차량이 트립을 갈아탄다 (노선 형상이 방향별이라 15번은
 * did0 = seq 1~38, did1 = 39~75 이고 둘이 미금역에서 만난다 - 실측 차량당
 * 하루 3회). 엔진은 트립으로 버스를 식별하므로 갈아타는 순간 옛 트립의 버스가
 * 남고, 지워질 때까지 같은 차가 두 대로 그려진다.
 *
 * ⚠️ **조건을 넓히면 살아 있는 버스를 지운다.** 피드에 잠깐 안 보이는 버스는
 * 계속 그려야 한다(`busTrack` 이 궤적으로 굴린다) - 여기서 고르는 건 **그
 * 차량이 다른 트립으로 이미 화면에 있는** 경우뿐이다.
 *
 * @param {Iterable<Object>} buses - 지금 살아 있는 버스들 (`{id, trip, anchor}`)
 * @param {Set<string>} liveTripIds - 이번 피드에 있는 트립 id
 * @returns {Array<Object>} 지울 버스들
 */
export function staleTrips(buses, liveTripIds) {
    const all = [...buses],
        // 실시간 버스만 본다 - 시간표 버스는 `anchor` 가 없고 이 경로로 오지 않는다.
        rt = all.filter(b => b.anchor && b.id),
        onFeed = new Set(rt.filter(b => liveTripIds.has(b.trip.id)).map(b => b.id));

    return rt.filter(b => !liveTripIds.has(b.trip.id) && onFeed.has(b.id));
}

/**
 * `s1 -> s2` 를 그릴 구간과 그 안의 진행률.
 *
 * **뒤로 갈 때는 구간을 거꾸로 잡는다.** 셰이더 진행률은 늘 0->1 로 증가하고
 * 위치는 `lerp(from, to, 진행률)` 이라 `from > to` 로 주면 그대로 역주행
 * 보간이 된다. 그래서 `f2 > f1` 도 저절로 성립한다 - `span` 이 음수가 되어
 * 부호가 두 번 뒤집히기 때문이다. 이 부호 논리가 틀려도 화면에서만 보이므로
 * (`map.js` 안에 두면 검사할 수 없다) 여기 순수 함수로 두고 검사한다.
 *
 * @param {Array<number>} offsets - 정류장별 누적거리 (오름차순)
 * @param {number} s1 - 지금 그릴 누적거리
 * @param {number} s2 - 한 틱 뒤 누적거리
 * @returns {Object} `{index, length, f1, f2}` - `length` 는 뒤로 갈 때 음수다
 */
export function sectionFor(offsets, s1, s2) {
    const back = s2 < s1,
        lo = back ? s2 : s1,
        hi = back ? s1 : s2;

    let i = 0;

    while (i < offsets.length - 2 && offsets[i + 1] <= lo) {
        i++;
    }

    let j = i + 1;

    while (j < offsets.length - 1 && offsets[j] < hi) {
        j++;
    }

    // 앞으로 가면 i -> j, 뒤로 가면 j -> i.
    const a = back ? j : i,
        b = back ? i : j,
        from = offsets[a],
        span = offsets[b] - from,
        frac = s => {
            return span ? Math.min(Math.max((s - from) / span, 0), 1) : 0;
        };

    return {index: a, length: b - a, f1: frac(s1), f2: frac(s2)};
}

/**
 * 지금 f1, 한 틱 뒤 f2 가 되도록 엔진 애니메이션 파라미터를 푼다.
 *
 * 셰이더(gpgpu/compute-fragment.glsl)의 순항 구간은
 * `a = (elapsed - accelTime/2) / D` 다. 거기에 두 점을 꽂아 D 와 시작시각을
 * 얻는다. 검사가 그 식을 그대로 옮겨 확인한다.
 *
 * ⚠️ 구간 맨 앞(진행률 0.025 미만)은 가속 램프 안이라 구간의 0.6% 쯤 어긋난다.
 * 한 틱 뒤에 다시 계획하므로 안 쌓인다 - 램프를 없애면 가속도가 발산한다.
 *
 * @param {number} now - 지금 (엔진 시각 오프셋)
 * @param {number} horizon - 내다보는 시간 (ms)
 * @param {number} f1 - 지금의 구간 내 진행률
 * @param {number} f2 - 한 틱 뒤의 진행률 (f1 보다 커야 한다)
 * @returns {?Object} `{startTime, duration, accelerationTime, acceleration}`
 */
export function planSection(now, horizon, f1, f2) {
    if (!(f2 > f1) || !(horizon > 0)) {
        return null;
    }

    const duration = horizon / (f2 - f1),
        accelerationTime = duration * 0.05;

    return {
        startTime: now - (f1 * duration + accelerationTime / 2),
        duration: duration + accelerationTime,
        accelerationTime,
        acceleration: 1 / (duration * accelerationTime)
    };
}
